import {useCallback, useEffect, useRef, useState} from 'react'

import {REFINE_ERROR_CODES} from '../lib/refineApi'
import {MAX_STORY_LENGTH} from '../lifeStoryTools'

export const LIFE_STORY_REFINE_FLAG = 'life_story_ai_refine'

export const REFINE_STATUS = {IDLE: 'idle', GENERATING: 'generating', REVIEW: 'review'}

export const REFINE_MODES = {POLISH: 'polish', SHORTEN: 'shorten', ENRICH: 'enrich', CUSTOM: 'custom'}

export const MODE_LABELS = {polish: 'Polished', shorten: 'Shortened', enrich: 'Enriched', custom: 'Refined'}
export const MODE_PROGRESS = {polish: 'Polishing', shorten: 'Shortening', enrich: 'Enriching', custom: 'Refining'}

export const ORIGINAL_LABEL = 'Your words'
export const EDITED_LABEL = 'Your edits'

// Under this many words there isn't enough to refine (AC 2.6); a selection
// needs this many words to count as a passage (AC 2.5). Both mirror the API.
export const MIN_REFINE_WORDS = 8
export const MIN_SELECTION_WORDS = 3
// Anything that fits in an answer can be refined, whole or by selection;
// the API refuses more than this. Mirrors MAX_STORY_LENGTH.
export const MAX_REFINE_INPUT_LENGTH = MAX_STORY_LENGTH
// Enrich needs somewhere to grow: the API refuses it when the rest of the
// story leaves less than this much headroom over the target (mirrors
// LifeStoryRefinementService::ENRICH_MIN_HEADROOM).
export const ENRICH_MIN_HEADROOM = 1.1

/** Whether an Enrich of the selection (or the whole story) has room to grow inside the cap. */
export const roomToEnrich = ({textLength, selectionLength = null}) => {
  const target = selectionLength === null ? textLength : selectionLength
  const room = MAX_STORY_LENGTH - (textLength - target)

  return room >= Math.ceil(target * ENRICH_MIN_HEADROOM)
}

export const REFINE_NOTES = {
  success: 'Read it over and make sure it still sounds like you.',
  successWithRecording: 'Read it over and make sure it still sounds like you. Only the written story changed. Your voice recording stays as is.',
  // In place of the success note when the model left part of the request
  // undone rather than invent facts (TK-3268, AC 3.6).
  heldBack: 'To keep the story true to what you\u2019ve told us, I\u2019ve left it as written.',
  stopped: 'Stopped. Your story is just as you left it.',
  restored: 'Change restored.',
  errors: {
    [REFINE_ERROR_CODES.RATE_LIMITED]: 'Too many requests just now. Wait a moment, then try again. Your story is unchanged.',
    [REFINE_ERROR_CODES.REFUSED]: 'That change couldn\u2019t be made. Try a different kind of request. Your story is unchanged.',
    [REFINE_ERROR_CODES.TOO_LONG]: 'This story is long \u2014 select the part you\u2019d like to refine.',
    [REFINE_ERROR_CODES.TOO_SHORT]: 'Write a few more sentences first \u2014 there\u2019s not quite enough here to refine yet.',
    [REFINE_ERROR_CODES.NO_ROOM]: 'There isn\u2019t room for a longer version here. Try Shorten or Polish instead.',
    [REFINE_ERROR_CODES.UNAUTHORIZED]: 'Refining isn\u2019t available for this account right now. Your story is unchanged.',
    default: 'We couldn\u2019t finish refining your story. Your words are unchanged.'
  }
}

// Failures where trying the same request again can't help (AC 7.2, 7.3).
const NO_RETRY_CODES = [
  REFINE_ERROR_CODES.RATE_LIMITED,
  REFINE_ERROR_CODES.REFUSED,
  REFINE_ERROR_CODES.TOO_LONG,
  REFINE_ERROR_CODES.TOO_SHORT,
  REFINE_ERROR_CODES.NO_ROOM,
  REFINE_ERROR_CODES.UNAUTHORIZED
]

export const countWords = text => ((text || '').trim().match(/\S+/g) || []).length

/**
 * The "Refine" state machine for one prompt visit (TK-3204).
 *
 * The screen owns the textarea; this hook never holds the answer text. It
 * reads it through `getText` when a refine starts and hands the result back
 * through `applyText`, which the screen persists at once. Because nothing
 * is applied until a whole rewrite has landed and passed the checks, Stop
 * and every failure leave the words exactly as they were (AC 4.4, 7.1).
 *
 * Undo/redo hold `{text, label, produced, version}`, where `produced` (the
 * `{mode, scope}` of the refine that led *away* from that version) names the
 * refine an Undo of it undoes, and `version` is the marker that comes back
 * with it. Undo pushes the *current* text, including anything typed after
 * the refine, so Redo brings the edits back (AC 5.6).
 * History lives in memory only and is dropped with the screen.
 *
 * `request(attributes, signal)` performs the API call; it is injected so
 * the hook can be exercised without the network.
 */
const useRefineStory = ({getText, applyText, question, request, hasRecording = false, logEvent = () => {}}) => {
  const [status, setStatus] = useState(REFINE_STATUS.IDLE)
  const [label, setLabel] = useState(ORIGINAL_LABEL)
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [note, setNote] = useState(null)
  const [current, setCurrent] = useState(null)
  const [lastIsTop, setLastIsTop] = useState(false)
  const [showingChanges, setShowingChanges] = useState(false)
  // Keyed so repeating an announcement still lands: the live region remounts
  // and screen readers see a new text node.
  const [announcement, setAnnouncement] = useState({text: '', key: 0})
  // Names the refine result the text stands on (TK-3234): a fresh number for
  // every result that lands, including a Try again replacing the last one,
  // and the earlier number again on Undo/Redo. Typing doesn't change it.
  const [version, setVersion] = useState(0)

  const nextVersionRef = useRef(1)
  const versionRef = useRef(version)
  const controllerRef = useRef(null)
  const lastRequestRef = useRef(null)
  const statusRef = useRef(status)
  const labelRef = useRef(label)
  const undoRef = useRef(undoStack)
  const redoRef = useRef(redoStack)
  const hasRecordingRef = useRef(hasRecording)
  const logEventRef = useRef(logEvent)
  const getTextRef = useRef(getText)
  const applyTextRef = useRef(applyText)
  const requestRef = useRef(request)

  statusRef.current = status
  labelRef.current = label
  versionRef.current = version
  undoRef.current = undoStack
  redoRef.current = redoStack
  hasRecordingRef.current = hasRecording
  logEventRef.current = logEvent
  getTextRef.current = getText
  applyTextRef.current = applyText
  requestRef.current = request

  const announce = useCallback(text => setAnnouncement(previous => ({text, key: previous.key + 1})), [])

  const startNewVersion = useCallback(() => {
    const next = nextVersionRef.current

    nextVersionRef.current += 1
    setVersion(next)
  }, [])

  const eventPayload = useCallback((extra = {}) => {
    const last = lastRequestRef.current || {}

    return {wildcard: last.mode, scope: last.selection ? 'selection' : 'story', ...extra}
  }, [])

  // The payload for undoing/redoing a specific history entry: the refine that
  // produced the version after it, not necessarily the latest request.
  const producedPayload = useCallback(
    produced => (produced ? {wildcard: produced.mode, scope: produced.scope} : eventPayload()),
    [eventPayload]
  )

  const settleIdle = useCallback(() => {
    setShowingChanges(false)
    setNote(null)
    setStatus(REFINE_STATUS.IDLE)
  }, [])

  // While a Try again from the review bar is replacing the last result, that
  // result stays applied; a failure or Stop returns to reviewing it.
  const replacingRef = useRef(false)

  const fail = useCallback(code => {
    const failureCode = code || REFINE_ERROR_CODES.UPSTREAM_ERROR
    const reviewing = replacingRef.current

    setStatus(reviewing ? REFINE_STATUS.REVIEW : REFINE_STATUS.IDLE)
    replacingRef.current = false
    setNote({
      kind: 'error',
      code: failureCode,
      message: REFINE_NOTES.errors[failureCode] || REFINE_NOTES.errors.default,
      // Back in review the bar's Try again owns the retry (see `canRetry`),
      // so the note doesn't offer a second one.
      retry: !reviewing && !NO_RETRY_CODES.includes(failureCode)
    })
    announce('Refining failed. Your story is unchanged.')
    logEventRef.current('failed_life_story_refine', eventPayload({failure_reason: failureCode}))
  }, [announce, eventPayload])

  /**
   * `previous` is the version under the last result when Try again comes
   * from the review bar: the request is made from it, but the last result
   * stays in the box (and saved) until the replacement lands, so a Stop or a
   * failure loses nothing.
   */
  const run = useCallback(({mode, instruction = '', selection = null, regenerate = false, previous}) => {
    if (statusRef.current === REFINE_STATUS.GENERATING) return Promise.resolve()

    const replacing = Boolean(previous)
    const base = replacing ? previous.text : getTextRef.current()
    const start = selection ? selection.start : 0
    const end = selection ? selection.end : base.length
    const raw = base.slice(start, end)
    // Surrounding whitespace stays put so the rewrite splices back cleanly.
    const lead = raw.match(/^\s*/)[0]
    const trail = raw.match(/\s*$/)[0]
    const target = raw.trim()
    const before = `${base.slice(0, start)}${lead}`
    const after = `${trail}${base.slice(end)}`
    const prevLabel = previous ? previous.label : labelRef.current

    lastRequestRef.current = {mode, instruction, selection}
    replacingRef.current = replacing
    setCurrent({mode, scope: selection ? 'selection' : 'story'})
    setShowingChanges(false)
    setNote(null)
    setStatus(REFINE_STATUS.GENERATING)
    announce(`${MODE_PROGRESS[mode]} your ${selection ? 'selection' : 'story'}. Press Escape to stop.`)

    const controller = typeof AbortController === 'function' ? new AbortController() : {abort: () => {}, signal: undefined}

    controllerRef.current = controller

    const attributes = {
      mode,
      question,
      text: base,
      ...instruction ? {instruction} : {},
      ...selection ? {passage: target} : {},
      ...regenerate ? {regenerate: true} : {}
    }

    return requestRef.current(attributes, controller.signal).then(
      result => {
        // Stopped (or superseded) while in flight: Stop already settled the UI.
        if (controllerRef.current !== controller) return

        controllerRef.current = null

        const nextText = `${before}${(result.refinedText || '').trim()}${after}`

        // The API guarantees this; the check is belt-and-braces so a splice
        // can never push the answer past the cap the textarea enforces.
        // (`fail` still sees `replacingRef`, so a replacement that's too long
        // goes back to review like any other replacement failure.)
        if (nextText.length > MAX_STORY_LENGTH) {
          fail(REFINE_ERROR_CODES.NO_ROOM)

          return
        }

        replacingRef.current = false

        const produced = {mode, scope: selection ? 'selection' : 'story'}

        // A replacement takes the last result's place: the version under it
        // is already on the stack.
        if (!replacing) setUndoStack(stack => [...stack, {text: base, label: prevLabel, produced, version: versionRef.current}])
        setRedoStack([])
        startNewVersion()
        setLabel(`${MODE_LABELS[mode]}${selection ? ' selection' : ''}`)
        setLastIsTop(true)
        applyTextRef.current(nextText)
        setStatus(REFINE_STATUS.REVIEW)
        if (result.heldBack) {
          setNote({kind: 'info', code: 'held_back', message: REFINE_NOTES.heldBack})
        } else {
          setNote({
            kind: 'info',
            code: 'success',
            message: hasRecordingRef.current ? REFINE_NOTES.successWithRecording : REFINE_NOTES.success
          })
        }
        announce(`${MODE_LABELS[mode]}. Undo is available.`)
      },
      error => {
        if (controllerRef.current !== controller) return

        controllerRef.current = null
        fail(error && error.code)
      }
    )
  }, [question, announce, fail, startNewVersion])

  const refine = useCallback(({mode, instruction = '', selection = null}) => run({mode, instruction, selection}), [run])

  /** Stop / Escape: the rewrite is discarded; the words were never touched (AC 4.4). */
  const stop = useCallback(() => {
    const controller = controllerRef.current

    if (!controller) return

    controllerRef.current = null
    controller.abort()
    setStatus(replacingRef.current ? REFINE_STATUS.REVIEW : REFINE_STATUS.IDLE)
    replacingRef.current = false
    setNote({kind: 'info', code: REFINE_ERROR_CODES.CANCELLED, message: REFINE_NOTES.stopped})
    announce('Stopped. Your story is unchanged.')
    logEventRef.current('failed_life_story_refine', eventPayload({failure_reason: REFINE_ERROR_CODES.CANCELLED}))
  }, [announce, eventPayload])

  const undo = useCallback(() => {
    const stack = undoRef.current

    if (!stack.length) return

    const entry = stack[stack.length - 1]
    // Captured now, not inside the state updater: React runs updaters at
    // render time, after applyText has already swapped the words out. The
    // version going onto the redo stack was produced by the same refine.
    const currentVersion = {text: getTextRef.current(), label: labelRef.current, produced: entry.produced, version: versionRef.current}

    setRedoStack(redo => [...redo, currentVersion])
    setUndoStack(stack.slice(0, -1))
    setLabel(entry.label)
    setVersion(entry.version)
    setLastIsTop(false)
    applyTextRef.current(entry.text)

    // A failure or Stop note (and its Try again) described the version that
    // was just replaced, so it goes too: the mini Undo can be used while idle.
    if (statusRef.current === REFINE_STATUS.REVIEW) {
      settleIdle()
    } else {
      setShowingChanges(false)
      setNote(null)
    }

    announce('Undone. Redo is available.')
    logEventRef.current('undid_life_story_refine', producedPayload(entry.produced))
  }, [announce, producedPayload, settleIdle])

  const redo = useCallback(() => {
    const stack = redoRef.current

    if (!stack.length) return

    const entry = stack[stack.length - 1]
    const currentVersion = {text: getTextRef.current(), label: labelRef.current, produced: entry.produced, version: versionRef.current}

    setUndoStack(undoEntries => [...undoEntries, currentVersion])
    setRedoStack(stack.slice(0, -1))
    setLabel(entry.label)
    setVersion(entry.version)
    setLastIsTop(stack.length === 1)
    setShowingChanges(false)
    applyTextRef.current(entry.text)

    if (statusRef.current === REFINE_STATUS.REVIEW) setNote({kind: 'info', code: 'restored', message: REFINE_NOTES.restored})
    else setNote(null)

    announce('Redone.')
  }, [announce])

  /**
   * Try again. From the review bar the version under the last result goes
   * back through with `regenerate` so the answer can differ, and the new
   * answer replaces the last result when it lands (see `run`); from a
   * failure note the text was never changed, so it just runs again.
   */
  const tryAgain = useCallback(() => {
    const last = lastRequestRef.current

    if (!last) return

    let previous = null

    if (statusRef.current === REFINE_STATUS.REVIEW) {
      const stack = undoRef.current

      if (!stack.length) return

      previous = stack[stack.length - 1]
    }

    return run({...last, regenerate: true, previous})
  }, [run])

  /** Keep it, or any exit from the screen while reviewing (an implicit keep). */
  const keep = useCallback(({implicit = false} = {}) => {
    if (statusRef.current !== REFINE_STATUS.REVIEW) return

    settleIdle()
    logEventRef.current('kept_life_story_refine', eventPayload({implicit}))
  }, [eventPayload, settleIdle])

  /**
   * The writer typed: the review bar closes and the version is theirs again
   * (AC 5.5). A note left by a stop or failure no longer describes the text,
   * so it goes too, and any redo branch is dropped so Redo can never replace
   * words typed after an Undo.
   */
  const noteTyping = useCallback(() => {
    if (statusRef.current === REFINE_STATUS.REVIEW) {
      settleIdle()
      logEventRef.current('kept_life_story_refine', eventPayload({implicit: true}))
    } else {
      setShowingChanges(false)
      setNote(null)
    }

    if (redoRef.current.length) setRedoStack([])
    if (labelRef.current !== ORIGINAL_LABEL) setLabel(EDITED_LABEL)

    setLastIsTop(false)
  }, [eventPayload, settleIdle])

  const toggleChanges = useCallback(() => setShowingChanges(showing => !showing && undoRef.current.length > 0), [])

  const clearNote = useCallback(() => setNote(null), [])

  /**
   * Shows a note from another flow on this prompt (the Enrich interview
   * ends with one). The note may carry its own `onRetry`, which the note
   * strip calls instead of Try again on the last refine.
   */
  const showNote = useCallback(nextNote => {
    setShowingChanges(false)
    setNote(nextNote)
  }, [])

  /**
   * Applies a version produced outside the refine request cycle (the Enrich
   * interview's woven story). The current words go onto the undo stack under
   * `produced` so the mini Undo/Redo, Show changes and the analytics treat it
   * like any refine; the screen is left idle with `note`, as the prototype
   * does after "Use this version" (no review bar, no Try again).
   */
  const applyExternal = useCallback(({text, label: nextLabel, produced, note: nextNote = null}) => {
    // The same cap `run` holds its own results to: the box's `maxLength`
    // does not truncate a value set from code, and this version is saved at
    // once. The caller is expected to have refused it already; this is the
    // last line, and it fails the same way a too-long refine does. Checked
    // before anything else is touched, so a refusal leaves a refine that is
    // still in flight to finish on its own.
    if (text.length > MAX_STORY_LENGTH) {
      setNote({kind: 'error', code: REFINE_ERROR_CODES.NO_ROOM, message: REFINE_NOTES.errors.no_room, retry: false})
      announce('Refining failed. Your story is unchanged.')
      logEventRef.current('failed_life_story_refine', {wildcard: produced.mode, scope: produced.scope, failure_reason: REFINE_ERROR_CODES.NO_ROOM})

      return false
    }

    // A refine still in flight would land on top of this version; drop it
    // (its handlers bail once the controller no longer matches).
    if (controllerRef.current) {
      controllerRef.current.abort()
      controllerRef.current = null
      replacingRef.current = false
    }

    const base = getTextRef.current()

    // The interview is not a request this hook can re-send (prototype:
    // `state.last = null`), so nothing here may Try again on it.
    lastRequestRef.current = null
    setCurrent({mode: produced.mode, scope: produced.scope})
    setUndoStack(stack => [...stack, {text: base, label: labelRef.current, produced, version: versionRef.current}])
    setRedoStack([])
    startNewVersion()
    setLabel(nextLabel)
    setLastIsTop(false)
    setShowingChanges(false)
    applyTextRef.current(text)
    setStatus(REFINE_STATUS.IDLE)
    setNote(nextNote)
    announce(`${nextLabel}. Undo is available.`)

    return true
  }, [announce, startNewVersion])

  // Escape stops a refine from anywhere on the screen (AC 4.4).
  useEffect(() => {
    if (status !== REFINE_STATUS.GENERATING) return undefined

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        stop()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [status, stop])

  // Leaving the screen mid-refine drops the request; there is nothing to apply to.
  useEffect(() => () => {
    if (controllerRef.current) {
      controllerRef.current.abort()
      controllerRef.current = null
    }
  }, [])

  const previousEntry = undoStack.length ? undoStack[undoStack.length - 1] : null

  return {
    status,
    label,
    mode: current ? current.mode : null,
    scope: current ? current.scope : null,
    note,
    announcement,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    // Which refine result the text stands on, so the screen can tell whether
    // a recording was made of the words as they read now (TK-3234).
    version,
    // No retry after a failure that trying again can't fix (the story is at
    // its limit, the writer is rate limited...).
    canRetry: Boolean(lastRequestRef.current) && undoStack.length > 0 && lastIsTop &&
      !(note && note.kind === 'error' && NO_RETRY_CODES.includes(note.code)),
    canShowChanges: undoStack.length > 0,
    showingChanges: showingChanges && Boolean(previousEntry),
    previousText: previousEntry ? previousEntry.text : null,
    hasHistory: undoStack.length > 0 || redoStack.length > 0,
    announce,
    refine,
    stop,
    undo,
    redo,
    tryAgain,
    keep,
    noteTyping,
    toggleChanges,
    clearNote,
    showNote,
    applyExternal
  }
}

export default useRefineStory
